import { Button, CopyToClipboard, DataTable, ColManager, DialogHandler, Dropdown, EmptyContent, Icon, ListTemplateModel, LoadingContainer, SubNav, TNumStr, TemplateModel, getCalendarFormat, getStorage, setStorage, Chip, TemplatesFactory, fetchRoute } from "@cakemail-org/ui-components-v2"
import { TCopyToClipboard } from "@cakemail-org/ui-components-v2/dist/esm/components/copyToClipboard/types"
import { TDataTableColumnsData, TDataTableRowData } from "@cakemail-org/ui-components-v2/dist/esm/components/dataTable/types"
import classNames from "classnames"
import { useContext, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import "./styles.scss"
import React from "react"
import { TTemplatesTable } from "./types"
import { ERoutes } from "../../../utils/url"
import { Stack } from "@mui/material"
import { TemplatesContainerContext } from "../../../container/templates/context"
import { showDeleteTemplateDialog } from "../../../container/templates/utils"
import { NameTableCol } from "../../tables/nameTableCol"
import { TagsTableCol } from "../../tables/tagsTableCol"


export function TemplatesTable({
    loading = false,
    reloadTable,
    handlePaginationChange,
    handleSortChange,
    handleChangeTagFilter,
    tag = "all",
    orderBy
}: TTemplatesTable) {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()

    const templatesContainerContext = useContext(TemplatesContainerContext)
    const copyToClipboardRef = useRef<TCopyToClipboard>()
    const colStorageVisibilityKey = "templatesTableColVisibility"
    const [removedColumns, setRemovedColumns] = useState<string[]>(getStorage({ key: colStorageVisibilityKey }) || ["id", "updated_on"])
    const [selectedRows, setSelectedRows] = useState<TNumStr[]>([])

    const templates = templatesContainerContext?.templates
    const templatesData = templates?.data


    function handleCopyClipBoard(id: string) {
        copyToClipboardRef?.current?.copy(id)
        DialogHandler.show({ title: t("copied-clipboard") })
    }

    function handleRowClick(event: React.MouseEvent<unknown>, template: TDataTableRowData) {
        navigate(fetchRoute({ route: ERoutes.templateDetails, params: { templateId: template.id } }), { state: { templateId: template.id } })
    }

    function handleRemovedColumns(removedRows: string[]) {
        setStorage({ key: colStorageVisibilityKey, data: removedRows })
        setRemovedColumns(removedRows)
    }

    function handleOpenAsset(template: ListTemplateModel) {
        navigate(fetchRoute({ route: ERoutes.templateDetails, params: { templateId: template.id } }), { state: { templateId: template.id } })
    }

    function handleRowSelect(event: React.MouseEvent<unknown> | React.ChangeEvent<HTMLInputElement>, id?: TNumStr, ids?: TNumStr[]) {
        setSelectedRows(ids || [])
    }

    function duplicateHandler(template: ListTemplateModel) {
        template
            .get()
            .then((data: TemplateModel) => {
                return TemplatesFactory.create({
                    template: {
                        name: data.name,
                        tags: data.tags,
                        content: data.content,
                        description: data.description
                    }
                })
            })
            .then(() => {
                reloadTable()
                DialogHandler.show({
                    type: "warning",
                    title: t("template-duplicate-success", { name: template.name })
                })
            })
            .catch(({ msg }) => {
                DialogHandler.show({
                    type: "warning",
                    title: t(msg || "general-error-action")
                })
            })
    }

    function handleShare(template: ListTemplateModel) {
        template?.share({}).then(() => {
            reloadTable()
        }).catch(({ msg }) => {
            DialogHandler.show({
                type: "warning",
                title: t(msg || "template-share-error")
            })
        })
    }

    function handleUnshare(template: ListTemplateModel) {
        template?.unshare().then(() => {
            reloadTable()
        }).catch(({ msg }) => {
            DialogHandler.show({
                type: "warning",
                title: t(msg || "template-unshare-error")
            })
        })
    }

    function getColumns(filtered: boolean = false): Array<TDataTableColumnsData> {

        let nameColKey = tag
        if (tag === "paid") {
            nameColKey = "paid"
        } else if (tag === "smart") {
            nameColKey = "smart-template"
        }

        const columns = [
            {
                id: "id",
                label: "Id",
                width: 100,
                sortable: true,
            },
            {
                id: "name",
                label: t("general-" + nameColKey),
                width: 525,
                sortable: true,
            },
            {
                id: "shared",
                label: t("general-published"),
                width: 200
            },
            /*{
                id: "sharing",
                label: t("templates-sharing-with"),
                width: 250
            },*/
            {
                id: "tags",
                label: t("general-tag_other"),
                with: 350
            },
            {
                id: "updated_on",
                label: t("updated-on"),
                sortable: true,
            },
            {
                id: "created_on",
                label: t("created-on"),
                sortable: true,
            },
            {
                id: "is_owner",
                label: t("general-ownership")
            },
            {
                id: "actions",
                label: ""
            }
        ]

        if (filtered) {
            return columns.filter((col) => !removedColumns.includes(col.id))
        }
        return columns
    }


    const colsData = getColumns(true)

    colsData[colsData.length - 1].label = <ColManager removedColumns={removedColumns} columns={getColumns().slice(0, -1)} onVisibilityChange={handleRemovedColumns} />

    const rows = templatesData?.map((template: ListTemplateModel) => {
        const isSharing = template?.sharing?.shared
        const isOwner = template.is_owner
        const DDOptions = [
            {
                isAction: true,
                text: t(isSharing ? "general-unpublish" : "general-publish"),
                onClick: () => {
                    isSharing ? handleUnshare(template) : handleShare(template)
                }
            },
            {
                isAction: true,
                text: t("general-duplicate"),
                onClick: () => {
                    duplicateHandler(template)
                }
            },
            { isAction: true, text: t("copy-id") + " : " + template.id, onClick: () => { handleCopyClipBoard(String(template.id)) } },
            {
                isAction: true, text: t("general-delete"), onClick: () => {
                    showDeleteTemplateDialog({ template })
                        .then(() => {
                            reloadTable()
                        })
                },
                disabled: !isOwner,
            }
        ]

        return {
            ...template,
            name: {
                id: template.name,
                content: <NameTableCol name={template.name} desc={template.description} imgSrc={template.thumbnail_url} />
            },
            tags: <TagsTableCol tags={template.tags} />,//truncateText(template.tags.join(", "), 20),
            shared: template?.sharing?.shared ? <Chip label={t("general-published")} icon={<Icon name="Checkmark10" />} /> : <Chip label={t(isSharing === null ? "template-sharing-legacy-share" : "general-unpublished")} color="pebble" />,
            //sharing: <p>{template.sharing.accounts.toString()}</p>,
            updated_on: template.updated_on !== 0 ? getCalendarFormat({ unixDate: template.updated_on, showTime: false }) : "",
            created_on: template.created_on !== 0 ? getCalendarFormat({ unixDate: template.created_on, showTime: false }) : "",
            is_owner: isOwner ? <Chip variant="filled" label={t("general-owned")} color="body1" /> : <Chip variant="outlined" label={t("general-not-owned")} color="body1" />,
            actions: {
                id: "actions",
                content: <>
                    <Button size="small" color="body1" variant="outlined" onClick={(e, btn) => { handleOpenAsset(template) }}>{t("asset-table-open-BTN")}</Button>
                    <Dropdown
                        color="body1"
                        options={DDOptions}
                        size="small"
                        iconOnly
                    />
                </>
            }
        }
    }) as any || []

    const dataTablesClasses = classNames("extraColPadding", {
        "loading": loading
    })

    return <Stack className={`templatesTable ${loading ? "loading" : ""}`}>
        <CopyToClipboard ref={copyToClipboardRef} />
        <SubNav
            className={loading ? "loading" : ""}
            onClick={handleChangeTagFilter}
            initialSelected={tag}
            items={[
                { text: t("general-all"), name: "all" },
                { text: t("general-free"), name: "free" },
                { text: t("general-paid"), name: "paid" },
                { text: t("general-smart-template"), name: "smart" }
            ]}
        />


        <LoadingContainer loading={!templatesData}>
            <>
                {templatesData && templatesData.length > 0 &&
                    <DataTable
                        initState={{ orderBy: orderBy }}
                        onRowClick={handleRowClick}
                        className={dataTablesClasses}
                        columnsData={colsData}
                        rows={rows}
                        stickyLastCol
                        //totalCount={templates?.pagination?.count ?? undefined}
                        //onPaginationChange={handlePaginationChange}
                        onSortChange={templates?.pagination?.count && templates?.pagination?.count >= 25 ? handleSortChange : undefined}
                    />
                }

                {templatesData?.length === 0 &&
                    <EmptyContent
                        title={t("templates-table-empty-" + tag)}
                    />
                }
            </>
        </LoadingContainer>
    </Stack>
}